// Help Desk — ticket list with status/priority/category chips + filters, a ticket
// detail view, and the Assignments manager. Backed by /tickets*, /tickets/meta,
// /agency-users, /assignments.
import { useEffect, useMemo, useState } from 'react';
import type { AgencyUser, Ticket } from '../../lib/suite-types';
import { agencyApi, type TicketsMetaResponse } from '../../lib/agencyApi';
import { Card, Chip, EmptyNote, ErrorNote, Select, Spinner } from './ui';
import TicketDetail from './TicketDetail';
import Assignments from './Assignments';
import Routing from './Routing';
import { formatApiDate } from '../../lib/time';

type Sub = 'tickets' | 'assignments' | 'routing';
const POT = '__pot__';

export default function HelpDesk() {
  const [sub, setSub] = useState<Sub>('tickets');
  const [meta, setMeta] = useState<TicketsMetaResponse | null>(null);
  const [users, setUsers] = useState<AgencyUser[]>([]);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([agencyApi.ticketsMeta(), agencyApi.agencyUsers()])
      .then(([m, u]) => {
        setMeta(m);
        setUsers(u);
      })
      .catch((e: Error) => setBootError(e.message));
  }, []);

  if (bootError) return <ErrorNote error={bootError} />;
  if (!meta) return <Spinner />;

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {([
          ['tickets', 'Tickets'],
          ['assignments', 'Assignments'],
          ['routing', 'Routing'],
        ] as [Sub, string][]).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setSub(id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              sub === id ? 'bg-brand text-brand-fg' : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {sub === 'tickets' && <TicketBoard meta={meta} users={users} />}
      {sub === 'assignments' && <Assignments users={users} />}
      {sub === 'routing' && <Routing users={users} />}
    </div>
  );
}

function TicketBoard({ meta, users }: { meta: TicketsMetaResponse; users: AgencyUser[] }) {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState(''); // '', POT, or user id
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    setTickets(null);
    setError(null);
    const params: { status?: string; assignee?: string } = {};
    if (statusFilter) params.status = statusFilter;
    if (assigneeFilter) params.assignee = assigneeFilter;
    agencyApi
      .tickets(params)
      .then((t) => setTickets(t))
      .catch((e: Error) => setError(e.message));
  }, [statusFilter, assigneeFilter, reloadKey]);

  const userName = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, u.name]));
    return (id: string | null) => (id ? map.get(id) ?? 'Unknown' : 'Generic pot');
  }, [users]);

  if (selected) {
    return (
      <TicketDetail
        ticketId={selected}
        meta={meta}
        users={users}
        onBack={() => setSelected(null)}
        onChanged={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={statusFilter} onChange={setStatusFilter}>
          <option value="">All statuses</option>
          {meta.statuses.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </Select>
        <Select value={assigneeFilter} onChange={setAssigneeFilter}>
          <option value="">All assignees</option>
          <option value={POT}>Unassigned (generic pot)</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </Select>
        {(statusFilter || assigneeFilter) && (
          <button
            onClick={() => {
              setStatusFilter('');
              setAssigneeFilter('');
            }}
            className="text-sm text-slate-500 underline hover:text-slate-700"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-sm text-slate-400">
          {tickets ? `${tickets.length} ticket${tickets.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>

      {error && <ErrorNote error={error} />}
      {!tickets && !error && <Spinner label="Loading tickets…" />}
      {tickets && tickets.length === 0 && (
        <EmptyNote>
          {statusFilter || assigneeFilter
            ? 'No tickets match these filters.'
            : "No support tickets yet. When your clients submit a ticket from the widget, it lands here for your team to work."}
        </EmptyNote>
      )}

      {tickets && tickets.length > 0 && (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Subject</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Assignee</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => {
                const status = meta.statuses.find((m) => m.id === t.status_id);
                const priority = meta.priorities.find((m) => m.id === t.priority_id);
                const category = meta.categories.find((m) => m.id === t.category_id);
                return (
                  <tr
                    key={t.id}
                    onClick={() => setSelected(t.id)}
                    className="cursor-pointer hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{t.subject}</div>
                      <div className="text-xs text-slate-400">{t.requester_name ?? t.requester_email ?? '—'}</div>
                    </td>
                    <td className="px-4 py-3">{status ? <Chip label={status.label} color={status.color} /> : '—'}</td>
                    <td className="px-4 py-3">{priority ? <Chip label={priority.label} color={priority.color} /> : '—'}</td>
                    <td className="px-4 py-3">{category ? <Chip label={category.label} muted /> : '—'}</td>
                    <td className="px-4 py-3">
                      {t.assigned_to ? (
                        <span className="text-slate-700">{userName(t.assigned_to)}</span>
                      ) : (
                        <Chip label="Generic pot" muted />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {formatApiDate(t.updated_at, '—')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
